import { z } from "zod";

import { accessioningNotesField } from "./accessioning-notes";
import { nullableTextField } from "./nullable-text-field";

const codeField = nullableTextField();

export const create = z.object({
  name: z.string(),
  code: codeField.create,
  orgType: z.enum(["PRIMARY_LAB", "SENDOUT_LAB", "CLIENT"]),
  invoiceEmail: z.email().nullish(),
  primarySelectable: z.boolean().prefault(true),
  selectable: z.boolean().prefault(true),
  ignoreInvoices: z.boolean().prefault(false),
  archiveType: z.enum(["OFFSITE", "RETAIN", "DISCARD"]),
  splitBillingOrgIds: z.number().array().prefault([]),
  archiveDuration: z.number().nullish(),

  // contact
  address: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  phone: z.string().nullish(),
  fax: z.string().nullish(),
  email: z.email().nullish(),

  // lab fields
  labDirector: z.string().nullish(),
  cliaNumber: z.string().nullish(),
  fdaNumber: z.string().nullish(),
  physician: z.string().nullish(),
});

const contactFields = z.object({
  address: z.string(),
  address2: z.string().nullish(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  phone: z.string().nullish(),
  fax: z.string().nullish(),
  email: z.email().nullish(),
});

const labFields = z.object({
  labDirector: z.string(),
  cliaNumber: z.string(),
  fdaNumber: z.string().nullish(),
  physician: z.string().nullish(),
  reportDisplayName: z.string().nullish(),
  requisitionDisplayName: z.string().nullish(),
  timezone: z.string().nullish(),
});

const clientFields = z.object({
  invoiceEmail: z.email(),
  primarySelectable: z.boolean().prefault(true),
  selectable: z.boolean().prefault(true),
  ignoreInvoices: z.boolean().prefault(false),
  archiveType: z.enum(["OFFSITE", "RETAIN", "DISCARD"]),
  splitBillingOrgIds: z.number().array().prefault([]),
  archiveDuration: z.number().nullish(),
  primaryLabId: z.number().nullish(),
  accessioningNotes: accessioningNotesField,
});

export const baseCreate = z.object({
  name: z.string(),
  code: codeField.create,
  orgType: z.enum(["PRIMARY_LAB", "SENDOUT_LAB", "CLIENT"]),
});

export const primaryLabCreate = z
  .object({
    orgType: z.literal("PRIMARY_LAB"),
    name: z.string(),
    code: codeField.create,
  })
  .extend(labFields.shape)
  .extend(contactFields.shape)
  .extend(clientFields.shape);

export const sendoutLabCreate = z
  .object({
    orgType: z.literal("SENDOUT_LAB"),
    name: z.string(),
    code: codeField.create,
  })
  .extend(labFields.shape)
  .extend(contactFields.shape)
  .extend(clientFields.shape);

export const clientCreate = z
  .object({
    orgType: z.literal("CLIENT"),
    name: z.string(),
    code: codeField.create,
  })
  .extend(clientFields.shape);

const primaryLabUpdate = primaryLabCreate
  .omit({ code: true })
  .extend({ code: codeField.update, id: z.number() });

const sendoutLabUpdate = sendoutLabCreate
  .omit({ code: true })
  .extend({ code: codeField.update, id: z.number() });

const clientUpdate = clientCreate
  .omit({ code: true })
  .extend({ code: codeField.update, id: z.number() });

export const orgCreate = z.discriminatedUnion("orgType", [
  primaryLabCreate,
  sendoutLabCreate,
  clientCreate,
]);

export const orgUpdate = z.discriminatedUnion("orgType", [
  primaryLabUpdate,
  sendoutLabUpdate,
  clientUpdate,
]);

export const update = create
  .omit({ code: true })
  .extend({ code: codeField.update, id: z.number() });
